import { ToolSwapAction } from './ToolSwapAction';
import { QuietPlacementTools } from './QuietPlacementTools';
/**
 * 連珠盤 (15x15) コンポーネント。
 *
 * - 連珠は **交点** に石を置くゲーム。
 * - 線は SVG で 14x14 グリッドの罫線として描く (= 15x15 交点)。
 * - クリック target (= button) は各交点に absolute 配置 + translate(-50%, -50%)
 *   で hit area が交点中心に来るようにする。
 * - 石はその button 内に絶対中央配置。
 */

import { useMemo, useState } from 'react';
import {
  hasSwap,
  renjuColorForSlot,
  renjuSwapStage,
  isPlace,
  legalKey,
  RENJU_SIZE,
  type RenjuMove,
  type RenjuState,
  type Side,
} from '../../games/renju';
import { usePendingConfirm } from '../../hooks/usePendingConfirm';
import { useFreshLastMove } from '../../lib/first-paint';
import {
  type GameState,
  getEngine,
  renjuForbiddenCells,
  renjuOpeningCells,
} from '../../wasm';
import { BoardCoords } from './BoardCoords';

interface Props {
  state: RenjuState;
  legal?: RenjuMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: RenjuMove) => void;
  mini?: boolean;
  lastMove?: RenjuMove;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  hideOwnSide?: boolean;
  /** 黒禁手セルの上書き。 渡されたら wasm 計算を使わずこの配列を使う (tutorial 用)。 */
  forbiddenCellsOverride?: [number, number][];
  /** チュートリアル用の交点強調。 */
  tutorialHighlight?: [number, number][];
  /** 開局規定で打てる点の上書き。 本体 wasm を積まない画面 (tutorial) 用。 */
  openingCellsOverride?: [number, number][];
  /** 仮選択プレビューの上書き。 `(row, col)` を打った場合に次手が置ける点を返す。 */
  openingPreviewOverride?: (row: number, col: number) => [number, number][];
  /**
   * 「相手がスワップしました」の通知を出すか。 既定は出す。
   *
   * tutorial のように 1 人で両方の手番を操作する画面では「相手」が居ないので
   * 切る。 `myTurnAllowed` を常に true にしている画面では、 自分が押した
   * スワップと相手が押したスワップを区別できないため。
   */
  swapNotice?: boolean;
}

/// 連珠盤の星 (花型 + 天元): (3,3) (3,11) (7,7) (11,3) (11,11)。
const STAR_POINTS: [number, number][] = [
  [3, 3],
  [3, 11],
  [7, 7],
  [11, 3],
  [11, 11],
];

const N = RENJU_SIZE; // 15

export function RenjuBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  hideOwnSide,
  forbiddenCellsOverride,
  tutorialHighlight,
  openingCellsOverride,
  openingPreviewOverride,
  swapNotice: swapNoticeEnabled = true,
}: Props) {
  // 盤を開いた瞬間は直前手のリングを出さない (mount 後の新しい手だけアニメする)。
  const freshLast = useFreshLastMove(lastMove);
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === renjuColorForSlot(state, state.side);
  const legalSet = new Set(
    (legal ?? [])
      .filter(isPlace)
      .map((m) => legalKey(m.Place.row, m.Place.col)),
  );
  const lastKey = lastMove && isPlace(lastMove)
    ? legalKey(lastMove.Place.row, lastMove.Place.col)
    : null;

  const showOppHint = !mini && !staticView && !isMyTurn && state.outcome == null;
  const swapAvailable = !mini && !staticView && yourSide != null && isMyTurn && state.outcome == null && onMove != null && renjuSwapStage(state) && hasSwap(legal);
  const { pending: pendingTap, tap: handleTap } = usePendingConfirm<string>(state);
  const pendingColor = yourSide ?? renjuColorForSlot(state, state.side);

  // 黒禁手 (三三/四四/長連) のセルを wasm から取得して × 表示。
  // mini や終局時は出さない。 override が渡されたらそれを優先。
  // 開局規定で今おける領域。 1〜3 手目は中央の正方形、 4 手目は珠形ごとの
  // 合法表で決まる不定形、 5 手目は中央 9x9。 6 手目以降は空 (制限なし)。
  const openingSet = useMemo<Set<string>>(() => {
    if (mini || state.outcome != null) return new Set();
    if (openingCellsOverride) {
      return new Set(openingCellsOverride.map(([r, c]) => `${r}-${c}`));
    }
    try {
      return new Set(renjuOpeningCells(state).map(([r, c]) => `${r}-${c}`));
    } catch {
      return new Set();
    }
  }, [state, mini, openingCellsOverride]);

  // 仮選択中は、 その手を打った場合に次の手が置ける領域を別色で重ねる。
  // 3 手目を選んでいる間に「この珠形にすると白 4 手目がどこに残るか」を
  // 見られるようにするのが狙い。 領域は珠形ごとに形が変わるので、 打つ前に
  // 見えないと選べない。
  const previewSet = useMemo<Set<string>>(() => {
    if (mini || state.outcome != null || pendingTap == null) return new Set();
    const at = pendingTap.split('-').map(Number);
    if (at.length !== 2) return new Set();
    const [pr, pc] = at;
    if (openingPreviewOverride) {
      return new Set(openingPreviewOverride(pr, pc).map(([r, c]) => `${r}-${c}`));
    }
    try {
      const next = getEngine('Renju').apply(state as GameState, {
        Place: { row: pr, col: pc },
      }) as RenjuState;
      return new Set(renjuOpeningCells(next).map(([r, c]) => `${r}-${c}`));
    } catch {
      return new Set();
    }
  }, [state, mini, pendingTap, openingPreviewOverride]);

  const forbiddenSet = useMemo<Set<string>>(() => {
    if (mini || state.outcome != null) return new Set();
    if (forbiddenCellsOverride) {
      return new Set(forbiddenCellsOverride.map(([r, c]) => `${r}-${c}`));
    }
    try {
      const cells = renjuForbiddenCells(state);
      return new Set(cells.map(([r, c]) => `${r}-${c}`));
    } catch {
      return new Set();
    }
  }, [state, mini, forbiddenCellsOverride]);

  // 自分の色を常時表示。 yourSide は **parity 込みの「打つ色」** を呼出側 (= Play /
  // Tutorial Runner) から渡してもらう契約。 First=黒、 Second=白 で素直に表示する。
  // (旧実装は state.swap_count parity を当コンポーネント内で適用していたが、 Play から
  // 渡される yourSide が既に sideOnBoard で parity 込みになっていたため二重反転バグ
  // が発生していた。 「色の変換」 は呼出側に統一する。)
  const yourColorLabel =
    yourSide === 'First' ? '黒 (先手)' : yourSide === 'Second' ? '白 (後手)' : null;
  const yourColorClass =
    yourSide === 'First' ? 'is-black' : yourSide === 'Second' ? 'is-white' : '';

  // 相手にスワップされた直後だけ出す。 スワップは手番を渡すので、 自分の番に
  // なっている = 相手が押した、 ということ。 自分が押した場合は相手の番に
  // なるのでここには出ない。
  // 消した通知が次のスワップで出直すよう、 局面ごとの鍵で覚える。
  const swapNoticeKey = `${state.plies}-${state.swap_count}`;
  const [dismissedSwapNotice, setDismissedSwapNotice] = useState<string | null>(null);
  const swapNotice =
    swapNoticeEnabled &&
    !mini &&
    !staticView &&
    state.outcome == null &&
    state.last_was_swap &&
    isMyTurn &&
    dismissedSwapNotice !== swapNoticeKey;
  const yourTurnLabel =
    yourSide === 'First' ? '黒番' : yourSide === 'Second' ? '白番' : null;

  // スワップは「自分がこれから打つはずだった色を相手に渡し、 相手の色を引き取る」。
  // 譲る側から見た文言に統一する。 スワップの機会は 1〜5 手目それぞれの直後に
  // あるので、 押す人は黒番のことも白番のこともある。


  // 領域の「外側」 = 盤の縁から、 領域を跨がずに辿り着けるセル。 盤の縁から
  // 塗りつぶして求める。 ここに入らない非領域セルは、 領域に完全に囲まれた穴。
  //
  // 1 マスずつ「四方が領域か」 で見ると、 石が 2 つ以上隣り合った塊を穴と判定
  // できない (互いに相手が領域でないため)。 連結成分で見る必要がある。
  const previewOutside = useMemo<Set<string>>(() => {
    const outside = new Set<string>();
    if (previewSet.size === 0) return outside;
    const stack: [number, number][] = [];
    const visit = (rr: number, cc: number) => {
      if (rr < 0 || rr >= N || cc < 0 || cc >= N) return;
      const k = legalKey(rr, cc);
      if (outside.has(k) || previewSet.has(k)) return;
      outside.add(k);
      stack.push([rr, cc]);
    };
    for (let i = 0; i < N; i++) {
      visit(0, i);
      visit(N - 1, i);
      visit(i, 0);
      visit(i, N - 1);
    }
    while (stack.length > 0) {
      const [rr, cc] = stack.pop() as [number, number];
      visit(rr - 1, cc);
      visit(rr + 1, cc);
      visit(rr, cc - 1);
      visit(rr, cc + 1);
    }
    return outside;
  }, [previewSet]);

  // プレビュー領域の辺を引くか。 隣も同じ領域なら引かない (輪郭だけを出すため)。
  //
  // 領域の外のセルでも、 領域に完全に囲まれた穴なら引かない。 石や仮選択の石が
  // 領域の中にあるとき、 そこを囲っても意味が無く、 輪郭の中に穴が浮いて
  // 読みにくくなるため。 逆に外側と繋がっているセルは普通に引く。 ここを省くと
  // 輪郭がその部分だけ途切れてしまう。
  const previewEdge = (rr: number, cc: number): number => {
    const inside = rr >= 0 && rr < N && cc >= 0 && cc < N;
    if (!inside) return 2;
    const k = legalKey(rr, cc);
    if (previewSet.has(k)) return 0;
    return previewOutside.has(k) ? 2 : 0;
  };

  // 交点位置 (パーセント): 左端 0%、 右端 100%、 14 等分。
  const pctOf = (i: number) => (i / (N - 1)) * 100;

  return (
    <div className={`renju-frame ${mini ? 'mini' : ''}`}>
      {!mini && !hideOwnSide && yourColorLabel && (
        <div className={`renju-side-indicator ${yourColorClass}`} data-testid="renju-your-color">
          <span className="renju-side-stone" aria-hidden />
          あなた: {yourColorLabel}
        </div>
      )}
      <div className={`renju-board ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
        <div className="renju-grid-area">
          {/* 座標は盤線の外 (木地) に。 交点の中に置くと石で隠れる。 */}
          {!mini && (
            <BoardCoords
              variant="intersection"
              files={Array.from({ length: N }, (_, c) => String.fromCharCode(65 + c))}
              ranks={Array.from({ length: N }, (_, r) => `${N - r}`)}
            />
          )}
          {/* 罫線: 14×14 のマスを引く = 15×15 交点 */}
          <svg
            className="renju-grid-svg"
            viewBox={`0 0 ${N - 1} ${N - 1}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            {Array.from({ length: N }, (_, i) => (
              <g key={`l${i}`}>
                <line x1={0} y1={i} x2={N - 1} y2={i} stroke="#1a1a1a" strokeWidth="0.04" />
                <line x1={i} y1={0} x2={i} y2={N - 1} stroke="#1a1a1a" strokeWidth="0.04" />
              </g>
            ))}
            {/* 星 / 天元 */}
            {STAR_POINTS.map(([r, c]) => (
              <circle key={`s${r}-${c}`} cx={c} cy={r} r={0.18} fill="#1a1a1a" />
            ))}
          </svg>

          {/* クリック target: 各交点に絶対配置 */}
          {state.board.flatMap((row, r) =>
            row.map((cell, c) => {
              const k = legalKey(r, c);
              const isLegal = isMyTurn && legalSet.has(k);
              const isForbidden = forbiddenSet.has(k);
              const isOpeningCell = openingSet.has(k);
              const isPreviewCell = previewSet.has(k);
              const isLast = !mini && lastKey === k;
              const isPendingTap = !mini && pendingTap === k;
              const isTutorialHint =
                tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
              const handler = () => {
                handleTap(k, isLegal, () => onMove?.({ Place: { row: r, col: c } }));
              };
              const stoneClass =
                cell === 'First' ? 'black' : cell === 'Second' ? 'white' : '';
              const pendingTapClass = isPendingTap
                ? pendingColor === 'First' ? 'pending-tap-black' : 'pending-tap-white'
                : '';
              return (
                <button
                  key={`${r}-${c}`}
                  className={`renju-intersection ${stoneClass} ${isLegal ? 'legal' : ''} ${isForbidden ? 'forbidden' : ''} ${isOpeningCell ? 'opening-allowed' : ''}  ${isLast ? 'last-move' : ''} ${isLast && !freshLast ? 'no-anim' : ''} ${pendingTapClass} ${isTutorialHint ? 'tutorial-hint' : ''}`}
                  style={{
                    left: `${pctOf(c)}%`,
                    top: `${pctOf(r)}%`,
                  }}
                  onClick={handler}
                  disabled={mini || !isLegal}
                  aria-label={`row ${r} col ${c}${isForbidden ? ' (forbidden)' : ''}`}
                >
                  {cell != null && <span key={cell} className="stone" />}
                  {isPendingTap && cell == null && <span className="pending-stone" aria-hidden />}
                  {isPreviewCell && cell == null && (
                    <span
                      className="renju-preview-edge"
                      style={{
                        borderTopWidth: previewEdge(r - 1, c),
                        borderRightWidth: previewEdge(r, c + 1),
                        borderBottomWidth: previewEdge(r + 1, c),
                        borderLeftWidth: previewEdge(r, c - 1),
                      }}
                      aria-hidden
                    />
                  )}
                  {isForbidden && cell == null && (
                    <span className="renju-forbidden-mark" aria-hidden />
                  )}
                </button>
              );
            }),
          )}
        </div>
        {swapNotice && (
          <button
            type="button"
            className="renju-swap-notice"
            data-testid="renju-swap-notice"
            onClick={() => setDismissedSwapNotice(swapNoticeKey)}
            aria-label="スワップの通知を閉じる"
          >
            <span className="renju-swap-notice-text">
              {yourTurnLabel
                ? `相手がスワップしました。\nあなたは${yourTurnLabel}です。`
                : '相手がスワップしました。'}
            </span>
            <span className="renju-swap-notice-close" aria-hidden>
              ✕
            </span>
          </button>
        )}
        <QuietPlacementTools game="Renju" yourSide={yourSide} mini={mini} staticView={staticView} />
      </div>
      {swapAvailable && <div className="tool-swap-row renju-tool-choice"><ToolSwapAction bowlAsset
        tool={yourSide === 'First' ? 'white' : 'black'}
        label={yourSide === 'First' ? '白に交換' : '黒に交換'} testId="renju-swap"
        onClick={() => onMove?.('Swap')} /></div>}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
      {!mini && state.outcome?.reason === 'Forbidden' && (
        <div className="renju-forbidden-hint">黒の禁手 — 白の勝ち</div>
      )}
      {!mini && state.outcome?.reason === 'Overline' && (
        <div className="renju-forbidden-hint">黒の長連 — 白の勝ち</div>
      )}
    </div>
  );
}

export const RENJU_BOARD_SIZE = RENJU_SIZE;
